import { Zap, Recycle, BarChart3, Leaf, ArrowRight } from "lucide-react";
import StandardSection from "@/components/ui/StandardSection";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/utils/authContext";

interface SustainabilitySectionProps {
  base_path: string;
  isExploreEnabled?: boolean;
}

export default function SustainabilitySection({ base_path, isExploreEnabled = true }: SustainabilitySectionProps) {
  const [loading, setLoading] = useState(false);
  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionPrimaryHeader, setSectionPrimaryHeader] = useState<string>("");
  const [sectionSecondaryHeader, setSectionSecondaryHeader] = useState<string>("");
  const [sectionDescription, setSectionDescription] = useState<string>("");
  const [card1Title, setCard1Title] = useState<string>("");
  const [card1Description, setCard1Description] = useState<string>("");
  const [card2Title, setCard2Title] = useState<string>("");
  const [card2Description, setCard2Description] = useState<string>("");
  const [card3Title, setCard3Title] = useState<string>("");
  const [card3Description, setCard3Description] = useState<string>("");

  const { isLoggedIn } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`
        );
        const data = await response.json();

        const landingPageData = data.find(
          (page: any) => page.name === "Landing Page"
        );
        const testimonialsData = landingPageData?.fields?.find((field: any) =>
          field?.key?.includes("highImpactModelPanel")
        );

        // Fetch section title and description
        const sectionTitleField = testimonialsData?.components?.find((comp: any) => comp.key === "carbon_title");
        const sectionPrimaryHeader = testimonialsData?.components?.find((comp: any) => comp.key === "carbon_primary_header");
        const sectionSecondaryHeader = testimonialsData?.components?.find((comp: any) => comp.key === "carbon_secondary_header");
        const sectionDescriptionField = testimonialsData?.components?.find((comp: any) => comp.key === "carbon_description");

        const card1Panel = testimonialsData?.components?.find((comp: any) => comp.key === "card1Panel");
        const card2Panel = testimonialsData?.components?.find((comp: any) => comp.key === "card2Panel");
        const card3Panel = testimonialsData?.components?.find((comp: any) => comp.key === "card3Panel");
        if (card1Panel) {
          const point1TitleField = card1Panel.components.find((comp: any) => comp.key === "card1_title");
          const point1SubtitleField = card1Panel.components.find((comp: any) => comp.key === "card1_description");
          setCard1Title(point1TitleField?.defaultValue || "");
          setCard1Description(point1SubtitleField?.defaultValue || "");
        }
        if (card2Panel) {
          const point2TitleField = card2Panel.components.find((comp: any) => comp.key === "card2_title");
          const point2SubtitleField = card2Panel.components.find((comp: any) => comp.key === "card2_description");
          setCard2Title(point2TitleField?.defaultValue || "");
          setCard2Description(point2SubtitleField?.defaultValue || "");
        }
        if (card3Panel) {
          const point3TitleField = card3Panel.components.find((comp: any) => comp.key === "card3_title");
          const point3SubtitleField = card3Panel.components.find((comp: any) => comp.key === "card3_description");
          setCard3Title(point3TitleField?.defaultValue || "");
          setCard3Description(point3SubtitleField?.defaultValue || "");
        }


        setSectionTitle(sectionTitleField?.defaultValue || "");
        setSectionPrimaryHeader(sectionPrimaryHeader?.defaultValue || "");
        setSectionSecondaryHeader(sectionSecondaryHeader?.defaultValue || "");
        setSectionDescription(sectionDescriptionField?.defaultValue || "");
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);


  return (
    <StandardSection id="sustainability" background="gradient">
      <div className="text-center space-y-8">
        {/* Button with side icon */}
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-3 sm:px-4 py-2 rounded-full text-sm shadow-sm mb-4">
            <Leaf className="h-4 w-4 text-green-600" />
            <span className="text-lg sm:text-xl leading-relaxed">{sectionTitle}</span>
            <Zap className="h-4 w-4 text-green-600" />
          </div>

        </div>

        {/* H2 Title */}
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
          <span className="text-gray-900 block">
            {sectionPrimaryHeader || "Impactful Advertising."}
          </span>
          <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent block">
            {sectionSecondaryHeader || "Minimal Footprint."}
          </span>
        </h2>

        {/* Sub-heading */}
        <p className="text-lg sm:text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
          {sectionDescription || "Welcome to the new era of Out-of-Home. Moving Walls Market is dedicated to connecting your brand with sustainable media options, making it simple to run effective campaigns that respect the planet."}
        </p>

        {/* Key Points with Icons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto mt-12">
          <div className="flex flex-col items-center gap-4 bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-green-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
              <Zap className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="text-gray-900 font-semibold text-lg mb-2">{card1Title || "Energy-Efficient Digital Networks"}</h3>
              <p className="text-gray-600 text-sm">{card1Description || "Powered by renewable energy sources and optimized for minimal power consumption"}</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4 bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-green-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
              <Recycle className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="text-gray-900 font-semibold text-lg mb-2">{card2Title || "Zero Physical Waste"}</h3>
              <p className="text-gray-600 text-sm">{card2Description || "Digital displays eliminate paper, vinyl, and other materials used in traditional advertising"}</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4 bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-green-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
              <BarChart3 className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="text-gray-900 font-semibold text-lg mb-2">{card3Title || "Precision Targeting, Fewer Resources"}</h3>
              <p className="text-gray-600 text-sm">{card3Description || "AI-driven campaigns reduce waste by targeting the right audience at the right time"}</p>
            </div>
          </div>
        </div>

        {/* Call-to-Action Button - Only show if explore is enabled */}
        {isExploreEnabled && (
          <div className="flex justify-center mt-12">
            <button
              className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              data-testid="button-explore-sustainable-options"
              onClick={() => {
                localStorage.setItem('clearStartCampaignFilters', 'true');
                if (isLoggedIn) {
                  navigate(`${base_path}/propertylist`);
                } else {
                  navigate(`${base_path}/explore`);
                }
              }}
            >
              Explore Sustainable Options
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </StandardSection>
  );
}